import React from 'react';
import {Link} from 'react-router-dom';
import InputWithIcon from '../../components/generics/input-with-icon';
import { Mail } from 'lucide-react';
import {useForgotPasswordViewModel} from "../../view-model/forgot-password-view-model";
import {observer} from "mobx-react-lite";
import { useNavigate } from "react-router";
import { useTranslation } from 'react-i18next';


const ForgotPassword: React.FC = observer(() => {
  const vienModel = useForgotPasswordViewModel()
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Récupérer la langue actuelle
  const currentLang = i18n.language;

  if (vienModel.getEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Représentation subtile de calendrier/planning en arrière-plan */}
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
          {/* Grille de planning stylisée */}
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(to right, rgb(35, 168, 146) 1px, transparent 1px),
              linear-gradient(to bottom, rgb(35, 168, 146) 1px, transparent 1px)
            `,
            backgroundSize: '80px 60px',
            transform: 'perspective(800px) rotateX(45deg) scale(1.5)',
            transformOrigin: 'center center',
          }}></div>
        </div>

        <div className="max-w-md w-full space-y-8 bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-gray-100 relative z-10 overflow-hidden">
          <div className="flex justify-center">
            <Link to={`/${currentLang}`}>
              <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
            </Link>
          </div>
          
          <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('forgotPassword.emailSentTitle')}</h1>
            <p className="text-gray-600 mb-6">
              {t('forgotPassword.emailSentText')}
            </p>
            <button 
              onClick={() => navigate(`/${currentLang}/login`)}
              className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              {t('forgotPassword.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Représentation subtile de calendrier/planning en arrière-plan */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
        {/* Grille de planning stylisée */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, rgb(35, 168, 146) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(35, 168, 146) 1px, transparent 1px)
          `,
          backgroundSize: '80px 60px',
          transform: 'perspective(800px) rotateX(45deg) scale(1.5)',
          transformOrigin: 'center center',
        }}></div>
      </div>

      <div className="max-w-md w-full space-y-8 bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-gray-100 relative z-10 overflow-hidden">
        <div className="flex justify-center">
          <Link to={`/${currentLang}`}>
            <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
          </Link>
        </div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">{t('forgotPassword.forgotPasswordTitle')}</h2>
          <p className="text-gray-600 text-center mb-6">{t('forgotPassword.forgotPasswordText')}</p>
          
          <form onSubmit={(e)=> vienModel.handleForgotPassword(e)} className="space-y-6">
            <div className="backdrop-blur-sm bg-white/80 rounded-lg p-2">
              <InputWithIcon
                label="Adresse email"
                type="email"
                id="email"
                name="email"
                value={vienModel.getEmail}
                onChange={(e) => vienModel.setEmail(e.target.value)}
                placeholder="votre@email.com"
                disableDarkMode
                required
                icon={<Mail size={18} />}
                error={vienModel.getErrorMessage ? vienModel.getErrorMessage : undefined}
              />
            </div>
            
            <button 
              type="submit" 
              disabled={vienModel.getisLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {vienModel.getisLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('forgotPassword.sendingLink')}
                </>
              ) : (
                t('forgotPassword.sendLink')
              )}
            </button>
            
            <div className="text-center mt-4">
              <Link to={`/${currentLang}/login`} className="text-sm text-primary hover:text-primary-dark transition-colors duration-200">
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
})

export default ForgotPassword;
